import { sanitizeUrl } from "./errors.js";
import { PlayerEventEmitter } from "./events.js";
import type {
  AspectMode,
  MediaTrack,
  Player,
  PlayerError,
  PlayerEvent,
  PlayerEventPayload,
  PlayerSource,
  PlayerState,
  PlayerStatus,
  TimeshiftWindow,
} from "./interface.js";

/**
 * The THIRD implementation of Player (after hls.ts and the cast adapter): the
 * APK's native engine, Media3 ExoPlayer, reached through the Capacitor plugin
 * native/android/.../NativePlayerPlugin.java (Section 22 Phase 1, step 2).
 *
 * ExoPlayer plays straight from the viewer's provider — no proxy, no server
 * transcode — and decodes MKV/TS/HLS and the device's audio codecs itself. The
 * rest of the app talks to this exactly as it talks to HlsPlayer.
 *
 * The picture is drawn by Android BEHIND the page. This class keeps the native
 * view on top of the page's video box: it reads the element's position every
 * animation frame and tells the plugin when it moves, and reports the same box
 * through `onBounds` so the page can cut a see-through window there.
 *
 * Engine selection is DETECTED, never assumed: nativePlayerBridge() returns
 * null anywhere but inside the APK, so the same bundle still runs as the PWA.
 */

const PLUGIN = "NativePlayer";

/** One "state" event from the plugin. */
export interface NativePlayerStateEvent {
  /** The slot this event is for; absent from older builds, meaning "main". */
  id?: string;
  state: "idle" | "buffering" | "ready" | "ended";
  playing: boolean;
  positionMs: number;
  /** -1 when unknown (a live stream). */
  durationMs: number;
  bufferedMs: number;
  live: boolean;
  error?: { code: string; message: string; httpStatus?: number };
  /**
   * Live TV's timeshift buffer, when the channel plays through one: how much is
   * saved behind the playhead and how far the playhead sits behind live.
   */
  shift?: { behindMs: number; offsetMs: number; windowMs: number };
  /** "starting", "on", or why live TV plays without rewind ("no-space", "unsupported", "failed"). */
  shiftStatus?: string;
}

/** The slice of Capacitor's page bridge this engine uses. Injectable for tests. */
export interface NativePlayerBridge {
  call(method: string, options?: Record<string, unknown>): Promise<unknown>;
  on(event: "state", callback: (data: NativePlayerStateEvent) => void): () => void;
  on(event: "pip", callback: (data: { active: boolean }) => void): () => void;
}

interface CapacitorGlobalLike {
  isNativePlatform?: () => boolean;
  PluginHeaders?: Array<{ name: string }>;
  nativePromise?: (plugin: string, method: string, options: Record<string, unknown>) => Promise<unknown>;
  addListener?: (plugin: string, event: string, callback: (data: never) => void) => { remove: () => unknown };
}

/**
 * The native engine's bridge when running inside the APK with the plugin
 * present, else null. Uses the page bridge Capacitor's shell injects
 * (window.Capacitor), so the web bundle needs no Capacitor package.
 */
export function nativePlayerBridge(): NativePlayerBridge | null {
  const cap = (globalThis as { Capacitor?: CapacitorGlobalLike }).Capacitor;
  if (cap?.isNativePlatform?.() !== true) return null;
  const { nativePromise, addListener } = cap;
  if (typeof nativePromise !== "function" || typeof addListener !== "function") return null;
  if (!cap.PluginHeaders?.some((header) => header.name === PLUGIN)) return null;
  return {
    call: (method, options) => nativePromise(PLUGIN, method, options ?? {}),
    on: (event: string, callback: (data: never) => void) => {
      const handle = addListener(PLUGIN, event, callback);
      return () => void handle.remove();
    },
  } as NativePlayerBridge;
}

/** What the device can do, from the plugin. */
export interface NativePlayerCapabilities {
  /** Android offers picture-in-picture here (most Fire TV devices do not). */
  pipSupported: boolean;
  /** How many live videos multiview should decode at once on this device (1-4). */
  deviceTiles: number;
  /** The screen has more than one refresh rate at its resolution, so frame-rate matching can act. */
  frameRateSwitching: boolean;
}

let capabilitiesCache: Promise<NativePlayerCapabilities> | null = null;

/** The device's capabilities, asked once. Conservative when the plugin cannot say. */
export function nativePlayerCapabilities(bridge: NativePlayerBridge): Promise<NativePlayerCapabilities> {
  capabilitiesCache ??= bridge
    .call("capabilities")
    .then((value) => {
      const result = value as Partial<NativePlayerCapabilities> | undefined;
      return {
        pipSupported: result?.pipSupported === true,
        deviceTiles: typeof result?.deviceTiles === "number" && result.deviceTiles >= 1 ? Math.floor(result.deviceTiles) : 2,
        frameRateSwitching: result?.frameRateSwitching === true,
      };
    })
    .catch(() => ({ pipSupported: false, deviceTiles: 2, frameRateSwitching: false }));
  return capabilitiesCache;
}

/**
 * Picture-in-picture in the APK: which player the floating window shows ("main",
 * or a multiview tile's playerId), and opening it. The window also opens by itself
 * when the viewer leaves the app while that player plays.
 */
/**
 * Tunnelled video in the APK: the TV chip times and shows frames straight from
 * the decoder. The viewer's setting, off by default; an older shell without the
 * method plays as before.
 */
export const nativeTunneling = {
  set: (bridge: NativePlayerBridge, enabled: boolean): Promise<unknown> =>
    bridge.call("setTunneling", { enabled }).catch(() => undefined),
};

/**
 * Auto frame-rate matching in the APK (G6): the screen switches to the video's frame rate.
 * The viewer's setting, "off" by default; "vod" is films and series, "all" live TV too. An older
 * shell without the method plays as before.
 */
export type FrameRateMatchMode = "off" | "vod" | "all";
export const nativeFrameRateMatch = {
  set: (bridge: NativePlayerBridge, mode: FrameRateMatchMode): Promise<unknown> =>
    bridge.call("setFrameRateMatch", { mode }).catch(() => undefined),
};

/**
 * Audio delay in the APK (G7), in ms from -500 to +500; positive delays the sound. 0 by
 * default, which changes nothing. An older shell without the method plays as before.
 */
export const nativeAudioDelay = {
  set: (bridge: NativePlayerBridge, ms: number): Promise<unknown> =>
    bridge.call("setAudioDelay", { ms }).catch(() => undefined),
};

/**
 * Timeshift for live TV in the APK (pause and rewind): the viewer's switch, on by
 * default, and the drive the buffer is kept on (an id from the DVR's volume list;
 * omitted means this device). Applies from the next channel. An older shell
 * without the method plays live TV directly, as before.
 */
export const nativeTimeshift = {
  set: (bridge: NativePlayerBridge, settings: { enabled: boolean; volumeId?: string }): Promise<unknown> =>
    bridge.call("setTimeshift", settings).catch(() => undefined),
};

export const nativePip = {
  setSource: (bridge: NativePlayerBridge, playerId: string): Promise<unknown> =>
    bridge.call("setPipSource", { id: playerId }).catch(() => undefined),
  /**
   * Whether leaving the app with a video playing floats it — the viewer's
   * setting. An older shell without the method keeps its own behaviour.
   */
  setAuto: (bridge: NativePlayerBridge, enabled: boolean): Promise<unknown> =>
    bridge.call("setAutoPip", { enabled }).catch(() => undefined),
  enter: async (bridge: NativePlayerBridge): Promise<boolean> => {
    try {
      const result = (await bridge.call("enterPip")) as { entered?: boolean } | undefined;
      return result?.entered === true;
    } catch {
      return false;
    }
  },
  /**
   * Leave the floating window. Best effort by design: the page calls this when
   * it has somewhere to put the picture again, and an older shell without the
   * method simply keeps floating instead of throwing.
   */
  exit: async (bridge: NativePlayerBridge): Promise<boolean> => {
    try {
      const result = (await bridge.call("exitPip")) as { exited?: boolean } | undefined;
      return result?.exited === true;
    } catch {
      return false;
    }
  },
};

/** Where the video box is, in CSS pixels relative to the viewport. */
export interface NativeVideoBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface NativePlayerOptions {
  /** The page element whose box the native video fills (the app's <video>). */
  video: HTMLElement;
  bridge: NativePlayerBridge;
  /**
   * Which native player this is: "main" (default) for the app's player, a unique
   * id per multiview tile. Each id is its own ExoPlayer and provider connection.
   */
  playerId?: string;
  /** Told the video box whenever it moves, or null when no video shows. */
  onBounds?: (bounds: NativeVideoBounds | null) => void;
  /** "slow" buffers more before starting and far more ahead — see the plugin. */
  bufferProfile?: "default" | "slow";
  /** Waits between retries of a failing stream, per URL, before moving to the next. */
  retryDelaysMs?: number[];
  /**
   * How long a URL may take to reach a first frame before it is treated as
   * dead and handed to the retry chain. Default 45s; tests pass a small value.
   */
  firstFrameTimeoutMs?: number;
  /**
   * The whole start's shared first-frame budget: the first URL, every retry and
   * every fallback URL together may not wait longer than this. Default 120s;
   * tests pass a small value.
   */
  firstFrameBudgetMs?: number;
  requestFrame?: (callback: () => void) => number;
  cancelFrame?: (handle: number) => void;
}

const DEFAULT_RETRY_DELAYS_MS = [1_000, 2_000, 4_000];

// No first frame within this long means the stream is not coming.
//
// ExoPlayer reports `buffering` from the moment a URL is loading, and that is
// indistinguishable from a picture that is about to arrive — so a provider that
// accepts the connection and then sends nothing usable left the viewer on
// "Loading…" forever, with no error and nothing to read (owner, 2026-09-23).
//
// 45s is ~3x the worst startup measured on this project (live HLS 10.7s, an MKV
// transcode 14.2s — docs/stream-telemetry.md), so it cannot cut off a slow but
// working channel; it only ends a start that was never going to finish.
const FIRST_FRAME_TIMEOUT_MS = 45_000;
// Every later attempt of the SAME load gets this instead: the first attempt
// covers the provider handshake (10-14s measured here), while a retry of a URL
// that already failed to start is not entitled to another 45s — otherwise a dead
// channel takes four attempts to say so.
const FIRST_FRAME_RETRY_GRACE_MS = 15_000;
// The BUDGET for one start, shared by every attempt above (see the web engine's
// FIRST_FRAME_BUDGET_MS for the reasoning — the two engines must not differ in
// how long they will make a viewer wait). A provider that lists several mirrors
// would otherwise multiply the per-attempt grace into minutes of "Retrying…".
const FIRST_FRAME_BUDGET_MS = 120_000;
// The plugin's own error codes are its own strings; this one is the page's,
// raised by the deadline below and turned into the `timeout` code when it ends
// the chain, so it is never mistaken for a spent free-tier allowance.
const FIRST_FRAME_CODE = "FIRST_FRAME_TIMEOUT";

export class NativePlayer implements Player {
  private readonly emitter = new PlayerEventEmitter();
  private readonly bridge: NativePlayerBridge;
  private readonly options: NativePlayerOptions;
  private readonly retryDelays: number[];
  private readonly firstFrameTimeoutMs: number;
  private readonly firstFrameBudgetMs: number;
  private readonly playerId: string;
  private readonly requestFrame: (callback: () => void) => number;
  private readonly cancelFrame: (handle: number) => void;
  private state: PlayerState = {
    status: "idle",
    currentTime: 0,
    duration: 0,
    bufferedAhead: 0,
    volume: 1,
    muted: false,
    attempt: 0,
  };
  private source: PlayerSource | undefined;
  /** The timeshift window from the last state event, while live TV plays through one. */
  private shift: NativePlayerStateEvent["shift"] | undefined;
  private shiftStatus: string | undefined;
  private urls: string[] = [];
  private urlIndex = 0;
  private attempt = 0;
  /** Bumped by every load/stop: stale retries and events check it. */
  private generation = 0;
  /** The current URL has played at least once — later buffering is a stall. */
  private started = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private firstFrameTimer: ReturnType<typeof setTimeout> | null = null;
  /** Attempts armed for the current load: the first gets the full deadline. */
  private firstFrameAttempts = 0;
  /** Where a film should be, in seconds: the last seek (Resume) for this source. A retry reloads from here. */
  private resumeAt = 0;
  /** When this start's shared budget runs out; no budget until a load begins. */
  private firstFrameBudgetEndsAt = Number.POSITIVE_INFINITY;
  private frame: number | null = null;
  /** The native view starts hidden, so "hidden" is the known starting state. */
  private boundsKey = "hidden";
  private destroyed = false;
  private readonly unsubscribe: () => void;

  constructor(options: NativePlayerOptions) {
    this.options = options;
    this.bridge = options.bridge;
    this.playerId = options.playerId ?? "main";
    this.retryDelays = options.retryDelaysMs ?? DEFAULT_RETRY_DELAYS_MS;
    this.firstFrameTimeoutMs = options.firstFrameTimeoutMs ?? FIRST_FRAME_TIMEOUT_MS;
    this.firstFrameBudgetMs = options.firstFrameBudgetMs ?? FIRST_FRAME_BUDGET_MS;
    this.requestFrame = options.requestFrame ?? ((callback) => requestAnimationFrame(callback));
    this.cancelFrame = options.cancelFrame ?? ((handle) => cancelAnimationFrame(handle));
    this.unsubscribe = this.bridge.on("state", (event) => {
      if ((event.id ?? "main") === this.playerId) this.onNativeState(event);
    });
    this.frame = this.requestFrame(this.followVideoBox);
  }

  async load(source: PlayerSource): Promise<void> {
    if (this.destroyed) return;
    this.generation += 1;
    this.clearRetry();
    this.source = source;
    this.urls = [source.url, ...(source.altUrls ?? [])];
    this.urlIndex = 0;
    this.attempt = 0;
    this.started = false;
    this.shift = undefined;
    this.shiftStatus = undefined;
    // A new source gets the full first-frame deadline, not the retry grace, and
    // its own budget — the previous channel's spent budget must not end this one.
    this.firstFrameAttempts = 0;
    this.resumeAt = 0;
    this.beginFirstFrameBudget();
    this.setState({
      status: "loading",
      source,
      mode: "native",
      currentTime: 0,
      duration: source.durationSeconds ?? 0,
      bufferedAhead: 0,
      attempt: 0,
      error: undefined,
    });
    await this.loadCurrentUrl();
  }

  async play(): Promise<void> {
    if (!this.source) return;
    await this.call("play");
  }

  pause(): void {
    void this.call("pause");
  }

  seek(seconds: number): void {
    if (!this.source || this.source.type === "live") return;
    const target = Math.max(0, seconds);
    this.resumeAt = target;
    this.setState({ currentTime: target });
    void this.call("seekTo", { positionMs: Math.round(target * 1000) });
  }

  /** What the timeshift buffer holds, from the plugin's last state event. */
  timeshift(): TimeshiftWindow {
    const shift = this.shift;
    if (this.source?.type !== "live") return { available: false, behindSeconds: 0, offsetSeconds: 0 };
    if (!shift) {
      const status = this.shiftStatus;
      const off = status && status !== "on" && status !== "starting" ? { offReason: status } : {};
      return { available: false, behindSeconds: 0, offsetSeconds: 0, ...off };
    }
    return {
      available: shift.behindMs > 1000,
      behindSeconds: shift.behindMs / 1000,
      offsetSeconds: shift.offsetMs / 1000,
    };
  }

  seekFromLiveEdge(offsetSeconds: number): void {
    if (this.source?.type !== "live" || !this.shift) return;
    const offsetMs = Math.round(Math.min(0, offsetSeconds) * 1000);
    // Shown at once, so a second press counts from where the first one went.
    const behindMs = Math.max(0, this.shift.behindMs + (this.shift.offsetMs + offsetMs));
    this.shift = { ...this.shift, behindMs, offsetMs: -offsetMs };
    void this.call("shiftSeek", { offsetMs });
  }

  setVolume(volume: number): void {
    const next = Math.max(0, Math.min(1, volume));
    this.setState({ volume: next });
    void this.call("setVolume", { volume: next });
  }

  setMuted(muted: boolean): void {
    this.setState({ muted });
    void this.call("setMuted", { muted });
  }

  updateDuration(seconds: number): void {
    if (!this.source) return;
    this.source = { ...this.source, durationSeconds: seconds };
    this.setState({ source: this.source, duration: this.state.duration > 0 ? this.state.duration : seconds });
  }

  stop(): void {
    if (this.destroyed) return;
    this.generation += 1;
    this.clearRetry();
    this.source = undefined;
    this.urls = [];
    void this.call("stop");
    this.setState({ status: "idle", source: undefined, currentTime: 0, duration: 0, bufferedAhead: 0, error: undefined });
  }

  /** ExoPlayer's track list for the current stream, read fresh each time. */
  async tracks(): Promise<MediaTrack[]> {
    const result = (await this.call("tracks").catch(() => null)) as { tracks?: MediaTrack[] } | null;
    return Array.isArray(result?.tracks) ? result.tracks : [];
  }

  selectTrack(kind: "audio" | "text", id: string | null): void {
    // `trackId`, NOT `id`: every call carries the SLOT id as `id`, which would
    // overwrite the track's (it did — subtitles silently turned off instead).
    void this.call("selectTrack", { kind, trackId: id }).catch(() => undefined);
  }

  setPlaybackRate(rate: number): void {
    void this.call("setPlaybackRate", { rate }).catch(() => undefined);
  }

  /** The picture is a native view, so ExoPlayer's resize mode is the aspect. */
  setAspectMode(mode: AspectMode): void {
    void this.call("setAspectMode", { mode }).catch(() => undefined);
  }

  getState(): PlayerState {
    return { ...this.state, source: this.state.source ? { ...this.state.source } : undefined };
  }

  on(event: PlayerEvent, callback: (payload: PlayerEventPayload) => void): () => void {
    return this.emitter.on(event, callback);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.stop();
    this.destroyed = true;
    this.unsubscribe();
    if (this.frame !== null) this.cancelFrame(this.frame);
    this.frame = null;
    this.options.onBounds?.(null);
    void this.call("release");
    this.emitter.clear();
  }

  /** A plugin call addressed to this player's slot. */
  private call(method: string, options: Record<string, unknown> = {}): Promise<unknown> {
    return this.bridge.call(method, { ...options, id: this.playerId });
  }

  private async loadCurrentUrl(): Promise<void> {
    const source = this.source;
    const url = this.urls[this.urlIndex];
    if (!source || !url) return;
    // Per URL, so a fallback URL gets the same fair chance the first one had.
    this.armFirstFrameTimer(source.type === "live" ? "live" : "vod");
    // A film reloaded by a retry carries on where it was, or where Resume sent it: the
    // retry used to start it again at 0:00 (onn box, 2026-10-20: a film resumed at 8:29
    // was slow to start, and the retry played it from the beginning).
    const resumeSeconds = source.type === "live" ? 0 : Math.max(this.resumeAt, this.state.currentTime);
    await this.call("load", {
      url,
      live: source.type === "live",
      ...(resumeSeconds >= 1 ? { startPositionMs: Math.round(resumeSeconds * 1000) } : {}),
      bufferProfile: this.options.bufferProfile ?? "default",
      // For the playback log (/playback in the remote debug tool): a name, never the URL.
      ...(source.title ? { title: source.title } : {}),
    });
  }

  /** Start the clock for one start, from its first attempt to its last. */
  private beginFirstFrameBudget(): void {
    this.firstFrameBudgetEndsAt = Date.now() + this.firstFrameBudgetMs;
  }

  /**
   * A deadline for this URL to reach a first frame.
   *
   * ExoPlayer's own `ready` state is the signal that a picture exists
   * (onNativeState clears this on it), so anything still loading when the
   * deadline passes is handed to the retry chain — the same path a plugin error
   * takes — which ends either in a recovered stream or in the viewer-facing
   * reason card. Without it a channel that never starts has no way out of
   * `loading` at all.
   *
   * Every attempt shares one budget (FIRST_FRAME_BUDGET_MS): an attempt whose
   * share would run past it is shortened to what is left, so mirrors cannot
   * stack their grace into minutes of "Retrying…".
   */
  private armFirstFrameTimer(kind: "live" | "vod"): void {
    this.clearFirstFrameTimer();
    const generation = this.generation;
    const perAttempt =
      this.firstFrameAttempts === 0
        ? this.firstFrameTimeoutMs
        : Math.min(this.firstFrameTimeoutMs, FIRST_FRAME_RETRY_GRACE_MS);
    this.firstFrameAttempts += 1;
    const remaining = this.firstFrameBudgetEndsAt - Date.now();
    const budgetCapped = remaining < perAttempt;
    const allowance = Math.max(0, Math.min(perAttempt, remaining));
    // The card reports the wait for the START, not for the final attempt: when
    // the budget is what ended it, the attempt's own short share is not the
    // number the viewer lived through.
    const waitedSeconds = Math.round((budgetCapped ? this.firstFrameBudgetMs : allowance) / 1000);
    this.firstFrameTimer = setTimeout(() => {
      this.firstFrameTimer = null;
      if (this.destroyed || generation !== this.generation || this.started) return;
      this.fail({
        code: FIRST_FRAME_CODE,
        message:
          kind === "live"
            ? `The channel did not start in time (${waitedSeconds}s).`
            : `Playback did not start in time (${waitedSeconds}s).`,
      });
    }, allowance);
  }

  private clearFirstFrameTimer(): void {
    if (this.firstFrameTimer === null) return;
    clearTimeout(this.firstFrameTimer);
    this.firstFrameTimer = null;
  }

  private onNativeState(event: NativePlayerStateEvent): void {
    const source = this.source;
    if (!source || this.destroyed) return;
    this.shift = event.shift;
    this.shiftStatus = event.shiftStatus;
    if (event.error) {
      this.fail(event.error);
      return;
    }
    // A live stream "ends" when the provider drops the connection: reconnect.
    if (event.state === "ended" && source.type === "live") {
      this.fail({ code: "LIVE_ENDED", message: "The live stream stopped." });
      return;
    }
    if (event.state === "ready") {
      this.started = true;
      this.attempt = 0;
      // A picture exists: the start deadline for this URL is spent.
      this.clearFirstFrameTimer();
    }
    const previous = this.state.status;
    let status: PlayerStatus = previous;
    if (event.state === "ended") status = "ended";
    else if (event.state === "buffering") status = this.started ? "buffering" : "loading";
    else if (event.state === "ready") status = event.playing ? "playing" : "paused";
    if (previous === "retrying" && event.state === "idle") status = "retrying";

    const currentTime = event.positionMs / 1000;
    const duration = event.durationMs > 0 ? event.durationMs / 1000 : (source.durationSeconds ?? 0);
    const bufferedAhead = Math.max(0, (event.bufferedMs - event.positionMs) / 1000);
    this.setState({ status, currentTime, duration, bufferedAhead, error: undefined });
    this.emitter.emit("timeupdate", { currentTime, duration, bufferedAhead });
    if (status !== previous) {
      if (status === "playing") this.emitter.emit("playing", this.getState());
      else if (status === "paused") this.emitter.emit("paused", this.getState());
      else if (status === "buffering") this.emitter.emit("buffering", this.getState());
      else if (status === "ended") this.emitter.emit("ended", this.getState());
    }
  }

  /**
   * Retry the current URL with backoff, then move to the next fallback URL,
   * then give up — the same shape as HlsPlayer's recovery, so a flaky channel
   * rides through blips and a dead one reports once instead of looping.
   */
  private fail(error: { code: string; message: string; httpStatus?: number }): void {
    const generation = this.generation;
    this.clearRetry();
    const delay = this.retryDelays[this.attempt];
    if (delay !== undefined) {
      this.attempt += 1;
    } else if (this.urlIndex < this.urls.length - 1) {
      this.urlIndex += 1;
      this.attempt = 0;
      this.started = false;
    } else {
      // Only here, with every retry of every address spent, does the viewer see an
      // error; a refusal or a dropped stream before this is ridden through quietly
      // as "retrying" (owner, 2026-10-19). A 403 while zapping is often the
      // provider's connection limit still counting the channel just left, and the
      // next attempt a second later plays.
      const playerError: PlayerError = {
        // A start the deadline ended is a TIMEOUT; everything else is UNAVAILABLE.
        // Never `exhausted`: that code is the app's free-tier meter, and sharing it
        // made a dead channel read as a spent allowance.
        code: error.code === FIRST_FRAME_CODE ? "timeout" : "unavailable",
        message: describeFailure(error),
        recoverable: false,
        ...(this.urls[this.urlIndex] ? { sourceUrl: sanitizeUrl(this.urls[this.urlIndex]) } : {}),
        ...(error.httpStatus !== undefined ? { httpStatus: error.httpStatus } : {}),
      };
      this.setState({ status: "error", error: playerError });
      this.emitter.emit("error", playerError);
      return;
    }
    this.setState({ status: "retrying", attempt: this.attempt });
    this.emitter.emit("retrying", this.getState());
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (generation !== this.generation) return;
      void this.loadCurrentUrl()
        .then(() => (generation === this.generation ? this.call("play") : undefined))
        .catch(() => undefined);
    }, delay ?? 0);
  }

  private clearRetry(): void {
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    // Both timers belong to "an attempt is in flight": every caller here
    // (load, stop, fail) is starting one, ending one, or replacing it.
    this.clearFirstFrameTimer();
  }

  private setState(patch: Partial<PlayerState>): void {
    this.state = { ...this.state, ...patch };
    this.emitter.emit("statechange", this.getState());
  }

  /**
   * Every frame: where is the page's video box now? Only a CHANGE goes to the
   * plugin, so a still video costs one getBoundingClientRect per frame and no
   * bridge traffic; a moving one (the mini-player being dragged) follows it.
   */
  private readonly followVideoBox = (): void => {
    if (this.destroyed) return;
    this.frame = this.requestFrame(this.followVideoBox);
    const element = this.options.video;
    let bounds: NativeVideoBounds | null = null;
    let onScreen: NativeVideoBounds | null = null;
    if (this.source && element.isConnected) {
      const rect = element.getBoundingClientRect();
      if (rect.width >= 2 && rect.height >= 2) {
        bounds = { x: Math.round(rect.left), y: Math.round(rect.top), width: Math.round(rect.width), height: Math.round(rect.height) };
        onScreen = toScreen(rect);
      }
    }
    const key = onScreen && bounds
      ? `${onScreen.x},${onScreen.y},${onScreen.width},${onScreen.height}|${bounds.x},${bounds.y}`
      : "hidden";
    if (key === this.boundsKey) return;
    this.boundsKey = key;
    void this.call("setBounds", onScreen ? { ...onScreen, visible: true } : { visible: false });
    this.options.onBounds?.(bounds);
  };
}

/**
 * A page rectangle in SCREEN terms, for the native view. getBoundingClientRect
 * is relative to the layout viewport; what the user sees is the visual
 * viewport, which a browser can pan or zoom inside it. On a phone, a top bar
 * 26px too wide made the page slide 26px sideways and 52px down, and the
 * native picture landed that far off its frame (2026-09-15). The page's own
 * see-through window stays in page coordinates (onBounds), which is right for
 * it; only the native view needs the screen.
 */
function toScreen(rect: { left: number; top: number; width: number; height: number }): NativeVideoBounds {
  const viewport = (globalThis as { visualViewport?: { scale: number; offsetLeft: number; offsetTop: number } | null }).visualViewport;
  const scale = viewport?.scale ?? 1;
  const offsetX = viewport?.offsetLeft ?? 0;
  const offsetY = viewport?.offsetTop ?? 0;
  return {
    x: Math.round((rect.left - offsetX) * scale),
    y: Math.round((rect.top - offsetY) * scale),
    width: Math.round(rect.width * scale),
    height: Math.round(rect.height * scale),
  };
}

/** A viewer-facing reason, never the stream URL. */
function describeFailure(error: { code: string; httpStatus?: number }): string {
  const status = error.httpStatus;
  if (status === 401 || status === 403) return `The provider refused this stream (HTTP ${status}). Check your subscription or connection limit.`;
  if (status === 404) return "The provider no longer has this stream (HTTP 404).";
  // Not standard codes, but IPTV panels send them for a spent or blocked account.
  if (status === 458 || status === 511 || status === 512 || status === 513) {
    return `The provider refused this stream (HTTP ${status}). The account may have expired or reached its connection limit.`;
  }
  if (status !== undefined && status >= 500) return `The provider's server failed (HTTP ${status}). Try again shortly.`;
  if (/FIRST_FRAME/i.test(error.code)) return "This stream did not start in time. It may be offline — try another, or try it again later.";
  if (/DECODING|DECODER|AUDIO_TRACK/i.test(error.code)) return "This device cannot decode this stream's format.";
  if (/NETWORK|TIMEOUT|IO_/i.test(error.code)) return "The stream could not be reached. Check your connection.";
  return "The stream could not be played.";
}
